import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Truck, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuppliersPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [formData, setFormData] = useState({ name: '', terms_days: 0, terms_type: 'from_invoice_date' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (supplier = null) => {
    if (supplier) {
      setEditingSupplier(supplier);
      setFormData({ name: supplier.name, terms_days: supplier.terms_days, terms_type: supplier.terms_type || 'from_invoice_date' });
    } else {
      setEditingSupplier(null);
      setFormData({ name: '', terms_days: 0, terms_type: 'from_invoice_date' });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Supplier name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingSupplier) {
        await axios.put(`${API}/suppliers/${editingSupplier.id}`, formData);
        toast.success('Supplier updated');
      } else {
        await axios.post(`${API}/suppliers`, formData);
        toast.success('Supplier created');
      }
      setDialogOpen(false);
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save supplier');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/suppliers/${id}`);
      toast.success('Supplier deleted');
      fetchSuppliers();
    } catch (error) {
      toast.error('Failed to delete supplier');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Suppliers
            </h1>
            <p className="text-slate-500 mt-1">
              Manage your suppliers and their payment terms
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="gap-2 bg-primary hover:bg-primary/90"
                onClick={() => handleOpenDialog()}
                data-testid="add-supplier-btn"
              >
                <Plus className="w-4 h-4" />
                Add Supplier
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {editingSupplier ? 'Edit Supplier' : 'Add Supplier'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Supplier Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter supplier name"
                    data-testid="supplier-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="terms_days">Payment Terms (days)</Label>
                  <Input
                    id="terms_days"
                    type="number"
                    min="0"
                    value={formData.terms_days}
                    onChange={(e) => setFormData({ ...formData, terms_days: parseInt(e.target.value) || 0 })}
                    placeholder="e.g., 30"
                    data-testid="supplier-terms-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Terms Calculated From</Label>
                  <Select
                    value={formData.terms_type}
                    onValueChange={(value) => setFormData({ ...formData, terms_type: value })}
                  >
                    <SelectTrigger data-testid="supplier-terms-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="from_invoice_date">From Bill Date</SelectItem>
                      <SelectItem value="from_end_of_month">From End of Month</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    {formData.terms_type === 'from_end_of_month' 
                      ? 'Days are calculated from the last day of the bill month'
                      : 'Days are calculated from the bill date'}
                  </p>
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline" data-testid="cancel-supplier-btn">Cancel</Button>
                </DialogClose>
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="save-supplier-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingSupplier ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {suppliers.length === 0 ? (
              <div className="empty-state py-16">
                <Truck className="empty-state-icon w-16 h-16 text-slate-300 mb-4" />
                <h3 className="empty-state-title text-lg font-semibold text-slate-900 mb-2">No suppliers yet</h3>
                <p className="empty-state-text text-sm text-slate-500 max-w-md">
                  Add your first supplier to start tracking bill payments and cash flow timing.
                </p>
              </div>
            ) : (
              <>
                <div className="p-4 border-b">
                  <Input
                    type="search"
                    placeholder="Search suppliers by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="max-w-sm"
                    data-testid="suppliers-search-input"
                  />
                </div>
              <div className="overflow-x-auto">
                <Table data-testid="suppliers-table">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Supplier Name</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-right">Payment Terms</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-right w-32">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {suppliers
                      .filter((s) => !searchQuery || (s.name || '').toLowerCase().includes(searchQuery.toLowerCase()))
                      .map((supplier) => (
                      <TableRow key={supplier.id} data-testid={`supplier-row-${supplier.id}`}>
                        <TableCell className="font-medium text-slate-900">{supplier.name}</TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {supplier.terms_days} days
                          <span className="text-xs text-slate-400 ml-1">
                            {supplier.terms_type === 'from_end_of_month' ? '(EOM)' : ''}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenDialog(supplier)}
                              data-testid={`edit-supplier-${supplier.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-supplier-${supplier.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Supplier</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete "{supplier.name}"? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(supplier.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                    data-testid={`confirm-delete-supplier-${supplier.id}`}
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
